/**
 * AffiliateProgram — public page where affiliates, vendors, and brokerages
 * can apply. Modern light aesthetic, matches the RentFast section design.
 * Route: /partners (canonical) + legacy alias /affiliate-program.
 */
import { useState } from "react";
import { Users, Store, Building2, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { toast } from "sonner";
import { usePageTitle } from "@/lib/seo";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const KINDS = [
  { key: "affiliate", label: "Affiliate", icon: Users,
    pitch: "You have an audience of Canadian landlords. Earn up to 20% commission on every paid signup for their first year." },
  { key: "vendor", label: "Vendor", icon: Store,
    pitch: "You service tenants (plumbing, cleaning, moving). Appear inside every landlord dashboard when they log a maintenance job." },
  { key: "brokerage", label: "Brokerage", icon: Building2,
    pitch: "You represent multi-unit landlords. Onboard their portfolios in bulk and earn a rev-share on every managed door." },
];

export default function AffiliateProgram() {
  usePageTitle("Partners & Affiliates — Central Rentals Canada");
  const [kind, setKind] = useState("affiliate");
  const [form, setForm] = useState({ name: "", email: "", company: "", audience: "", why: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!form.name || !form.email || form.why.trim().length < 20) {
      toast.error("Please fill in name, email, and a short 'why you' note (20+ chars).");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/affiliate/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, ...form }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Couldn't submit — please try again.");
      setDone(true);
      toast.success("Thanks — we'll review within 3 business days.");
    } catch (err) {
      toast.error(err.message || "Couldn't submit — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="affiliate-program-page">
      <PublicHeader light />
      <section className="max-w-5xl mx-auto px-5 py-12 md:py-16">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-700">
            <Users className="w-3.5 h-3.5"/> Partner with Central Rentals
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Grow with Canada&apos;s landlord OS.
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Whether you refer landlords, service their tenants, or manage their portfolios — we&apos;ve got a program for you. Commissions paid monthly via Stripe Connect. No dark patterns, no hidden clawbacks.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3">
          {KINDS.map((k) => {
            const Icon = k.icon;
            const active = kind === k.key;
            return (
              <button
                key={k.key}
                type="button"
                onClick={() => setKind(k.key)}
                data-testid={`kind-${k.key}`}
                className={`text-left p-5 rounded-2xl border-2 transition-all min-w-0 ${active ? "border-emerald-500 bg-emerald-50/50 shadow-lg" : "border-slate-200 hover:border-slate-300 bg-white"}`}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${active ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                  <Icon className="w-5 h-5"/>
                </div>
                <div className="mt-3 font-display text-lg font-extrabold text-slate-900">{k.label}</div>
                <div className="mt-1 text-sm text-slate-600 leading-snug">{k.pitch}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-10 max-w-2xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-8" data-testid="affiliate-apply-form">
          {done ? (
            <div className="text-center py-6" data-testid="affiliate-apply-thanks">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto"/>
              <h2 className="mt-3 font-display text-2xl font-extrabold">Application received</h2>
              <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
                Thanks — a real human reviews every submission within 3 business days. If you&apos;re approved, we&apos;ll email you a referral link and Stripe Connect onboarding link for payouts.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 gap-3">
              <h2 className="font-display text-xl font-extrabold text-slate-900">Apply as a <span className="text-emerald-700">{KINDS.find((k) => k.key === kind).label}</span></h2>
              <FormField label="Your name" required>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Priya Sharma" data-testid="apply-name" required />
              </FormField>
              <FormField label="Your email" required>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" data-testid="apply-email" required />
              </FormField>
              <FormField label={kind === "brokerage" ? "Brokerage name" : "Company / brand (optional)"}>
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="apply-company" />
              </FormField>
              <FormField label={kind === "affiliate" ? "How you reach landlords" : kind === "vendor" ? "Cities you service" : "Managed door count"}>
                <input value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} placeholder={kind === "affiliate" ? "Blog URL, newsletter, community" : kind === "vendor" ? "Edmonton, Calgary, Toronto…" : "e.g. 240 doors across Alberta"} data-testid="apply-audience" />
              </FormField>
              <FormField label="Why you'd be a great fit" required>
                <textarea rows={4} value={form.why} onChange={(e) => setForm({ ...form, why: e.target.value })} placeholder="What makes your audience or service a strong match?" data-testid="apply-why" required />
              </FormField>
              <button type="submit" disabled={busy} data-testid="apply-submit" className="mt-2 inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-extrabold text-sm">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Submitting…</> : <>Apply <ArrowRight className="w-4 h-4"/></>}
              </button>
              <p className="text-[11px] text-slate-500 text-center">A real human reviews every application. Approvals typically within 3 business days.</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-bold text-slate-700 mb-1">
        {label}{required && <span className="text-rose-600 ml-0.5">*</span>}
      </div>
      <div className="[&_input]:w-full [&_input]:rounded-lg [&_input]:border [&_input]:border-slate-200 [&_input]:px-3 [&_input]:py-2 [&_input]:text-sm [&_input]:outline-none [&_input:focus]:border-emerald-500 [&_input:focus]:ring-1 [&_input:focus]:ring-emerald-500 [&_textarea]:w-full [&_textarea]:rounded-lg [&_textarea]:border [&_textarea]:border-slate-200 [&_textarea]:px-3 [&_textarea]:py-2 [&_textarea]:text-sm [&_textarea]:resize-none [&_textarea]:outline-none [&_textarea:focus]:border-emerald-500 [&_textarea:focus]:ring-1 [&_textarea:focus]:ring-emerald-500">
        {children}
      </div>
    </label>
  );
}
